import * as z from 'zod';

import * as api from '@gitbook/api-client';
import { assertNever, type MandateProps } from '@gitbook/util-ts';

import { Timestamp } from './db/firestore';
import {
    GitSyncProviderTypeGitHubLegacySchema,
    GitSyncProviderTypeGitHubSchema,
    GitSyncProviderTypeGitLabSchema,
} from './spaces';

/**
 * Timeout for the sync operation in milliseconds.
 * If the updatedAt of the operation is older than this, the sync operation is considered as failed.
 *
 * Git Sync operations have a maximum timeout of 10 minutes.
 */
export const GITSYNC_OPERATION_TIMEOUT = 10 * 60 * 1000;

/** Which side is the main priority during the initial setup */
export enum GitSyncPriority {
    GITBOOK = 'gitbook',
    PROVIDER = 'provider',
}
export const GitSyncPrioritySchema = z.enum(GitSyncPriority);

/**
 * GitHub sync installation.
 */
export const GitSyncProviderGitHubSchema = z.object({
    type: GitSyncProviderTypeGitHubSchema,
    /** ID of the GitHub installation */
    installation: z.number(),
    /** Name of the account (for display only) */
    accountName: z.string(),
    /** Repository with which it's integrated */
    repository: z.number(),
    repositoryName: z.string(),
});
export type GitSyncProviderGitHub = z.infer<typeof GitSyncProviderGitHubSchema>;

/**
 * GitLab sync installation.
 */
export const GitSyncProviderGitLabSchema = z.object({
    type: GitSyncProviderTypeGitLabSchema,
    /** API access token */
    accessToken: z.string(),
    /** URL when using a custom instance */
    gitlabEndpoint: z.string().optional(),
    /** Project for the sync */
    project: z.number(),
    projectName: z.string(),
    /** ID of the webhook being configured during installation */
    hook: z.number().optional(),
});
export type GitSyncProviderGitLab = z.infer<typeof GitSyncProviderGitLabSchema>;

/**
 * GitHub sync installation configured before the release of GitBook-X.
 */
export const GitSyncProviderGitHubLegacySchema = z.object({
    type: GitSyncProviderTypeGitHubLegacySchema,
    /** Repository with which it's integrated */
    repository: z.string(),
    /** API access token */
    accessToken: z.string(),
    /** ID of the webhook being configured during installation */
    hook: z.number().optional(),
});
export type GitSyncProviderGitHubLegacy = z.infer<typeof GitSyncProviderGitHubLegacySchema>;

export const GitSyncProviderSchema = z.discriminatedUnion('type', [
    GitSyncProviderGitHubSchema,
    GitSyncProviderGitLabSchema,
    GitSyncProviderGitHubLegacySchema,
]);
export type GitSyncProvider = z.infer<typeof GitSyncProviderSchema>;

export type GitSyncProviders = {
    github: GitSyncProviderGitHub;
    gitlab: GitSyncProviderGitLab;
    'github-legacy': GitSyncProviderGitHubLegacy;
};

/**
 * GitSync installation for a space.
 * It represents the configuration created by a user to integrate the space with a Git provider.
 */
export const GitSyncInstallationSchema = z.object({
    /** ID of the space */
    space: z.string(),
    /**
     * Unique ID for the installation.
     * This ID is different when the installation is deleted and re-created.
     *
     * The key was originally not implemented in the data design and thus can be undefined for old installations.
     * It is now defined for all new installations.
     */
    key: z.string().max(100).optional(),
    /** The full git ref. Example: refs/heads/main. */
    ref: z
        .string()
        .max(99)
        .regex(/refs\/(heads|tags)\/.*/),
    /**
     * Root folder to use for monorepos with multiple spaces synced.
     */
    projectDirectory: z.string().optional(),
    /**
     * Template to use for commit messages.
     */
    commitMessageTemplate: z.string().max(300).optional(),
    /** Configuration to access the upstream provider */
    provider: GitSyncProviderSchema,
    /** Priority */
    priority: GitSyncPrioritySchema,
    /** Whether to generate preview from branches external to the repository */
    previewExternalBranches: z.boolean().optional(),
    /** The experimental version of gitsync to use for this installation */
    experimentalVersion: z.number().optional(),
    createdAt: z.instanceof(Timestamp),
    createdBy: z.string(),
});

const TypeGitSyncInstallation = <Provider extends z.ZodType<GitSyncProvider>>(provider: Provider) =>
    GitSyncInstallationSchema.extend({ provider });
type TypedGitSyncInstallation<Provider extends z.ZodType<GitSyncProvider>> = ReturnType<
    typeof TypeGitSyncInstallation<Provider>
>;
export type GitSyncInstallation<Provider extends GitSyncProvider = GitSyncProvider> = MandateProps<
    z.infer<TypedGitSyncInstallation<z.ZodType<Provider>>>,
    'provider'
>;

/**
 * Returns the base URL of a Git installation in the provider.
 */
export function getGitBaseURL(provider: GitSyncProvider): string {
    switch (provider.type) {
        case api.GitSyncProvider.Github:
            return `https://github.com/${provider.accountName}/${provider.repositoryName}`;
        case api.GitSyncProvider.GithubLegacy:
            return `https://github.com/${provider.repository}`;
        case api.GitSyncProvider.Gitlab: {
            const hostname = provider.gitlabEndpoint || 'https://gitlab.com';
            return `${hostname}/${provider.projectName}`;
        }
        default:
            return assertNever(provider);
    }
}

/**
 * Return the full repo name from a provider's information.
 */
export function getGitRepoName(provider: GitSyncProvider): string {
    switch (provider.type) {
        case api.GitSyncProvider.Github:
            return `${provider.accountName}/${provider.repositoryName}`;
        case api.GitSyncProvider.GithubLegacy:
            return provider.repository;
        case api.GitSyncProvider.Gitlab:
            return provider.projectName;
        default:
            return assertNever(provider);
    }
}

/**
 * Make a remote ref pretty (e.g. refs/heads/master => master)
 */
export function getPrettyGitRef(ref: string): string {
    return ref ? ref.replace('refs/', '').replace('heads/', '') : '';
}
